import { supabase } from '@/integrations/supabase/client';
export interface TLDRItem {
  text: string;
  tags: string[];
  source_url?: string;
}
export interface NewsItem {
  headline: string;
  summary: string;
  tags: string[];
  source_url?: string;
}
export interface CompanyNewsItem {
  company: string;
  items: { headline: string; summary: string; source_url?: string }[];
}
export interface SourceEvidence {
  url: string;
  name: string;
  published_at?: string | null;
  is_primary?: boolean;
}
export interface Signal {
  event_key: string;
  article_ids: string[];
  headline: string;
  fact: string;
  why_it_matters: string;
  work_implication: string;
  limits: string;
  tags: string[];
  sources: SourceEvidence[];
  source_url: string;
}
export interface PracticalTest {
  title: string;
  instructions: string;
  limits: string;
  sources: SourceEvidence[];
}
export interface Correction {
  date: string;
  text: string;
  source_url: string;
}
export interface NewsSource {
  id: string;
  name: string;
  url: string;
  type: string;
}
export interface Issue {
  id: string;
  issue_number: number;
  title: string;
  subtitle: string | null;
  published_at: string | null;
  status: string;
  tldr: TLDRItem[];
  market_updates: NewsItem[];
  research_section: NewsItem[];
  company_news: CompanyNewsItem[];
  created_at: string;
  updated_at: string;
  period_start?: string | null;
  period_end?: string | null;
  editorial_intro?: string | null;
  signals: Signal[];
  practical_test?: PracticalTest | null;
  editorial_checks: string[];
  reviewed_at?: string | null;
  corrections: Correction[];
}
export async function invoke<T = Record<string, unknown>>(
  name: string,
  body: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.functions.invoke(name, { body });
  if (error) {
    let detail = error.message;
    if ('context' in error && error.context instanceof Response) {
      try {
        const parsed = await error.context.json();
        detail = parsed.error || detail;
      } catch {
        /* Preserve the original transport error. */
      }
    }
    throw new Error(detail);
  }
  if (data?.error || data?.success === false)
    throw new Error(data.error || 'Operazione fallita');
  return data as T;
}
export function transformIssue(value: unknown): Issue {
  const data = value as Issue;
  return {
    ...data,
    tldr: Array.isArray(data.tldr) ? data.tldr : [],
    market_updates: Array.isArray(data.market_updates)
      ? data.market_updates
      : [],
    research_section: Array.isArray(data.research_section)
      ? data.research_section
      : [],
    company_news: Array.isArray(data.company_news) ? data.company_news : [],
    signals: Array.isArray(data.signals) ? data.signals : [],
    editorial_checks: Array.isArray(data.editorial_checks)
      ? data.editorial_checks
      : [],
    corrections: Array.isArray(data.corrections) ? data.corrections : [],
  };
}
export const newsletterApi = {
  async getPublishedIssues() {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('status', 'published')
      .order('issue_number', { ascending: false });
    if (error) throw error;
    const issues = (data || []).map(transformIssue);
    window.__NEWSLETTER_CACHE__ = issues;
    return issues;
  },
  async getIssueByNumber(number: number) {
    const { data, error } = await supabase
      .from('issues')
      .select('*')
      .eq('issue_number', number)
      .eq('status', 'published')
      .maybeSingle();
    if (error) throw error;
    const issue = data ? transformIssue(data) : null;
    if (issue) window.__NEWSLETTER_CACHE__ = [issue];
    return issue;
  },
  async getLatestIssue() {
    return (await this.getPublishedIssues())[0] || null;
  },
  subscribe(email: string) {
    return invoke('subscribe', { email });
  },
  confirm(token: string) {
    return invoke<{ message: string }>('confirm-subscription', { token });
  },
  unsubscribe(email: string, token?: string | null) {
    return invoke<{ message: string }>('unsubscribe', {
      email,
      token,
      manual: !token,
    });
  },
};
